/**
 * EBON Media Solutions - Client Email Dispatch Service
 * Sends all website inquiries to the backend SMTP router (info@ebonmedia.com)
 */

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  error?: string;
  recipient?: string;
  messageId?: string;
  leadId?: string;
}

export interface ContactFormData {
  fullName: string;
  email: string;
  phone?: string;
  company?: string;
  service: string;
  budget?: string;
  message: string;
  region?: string;
  timeline?: string;
}

export interface ConsultationFormData {
  name: string;
  email: string;
  phone: string;
  company?: string;
  service: string;
  preferredTime?: string;
  budget?: string;
  notes?: string;
}

export interface ProjectScopingFormData {
  service: string;
  projectGoal: string;
  projectType: string;
  targetTimeline: string;
  currency: string;
  budgetRange: string;
  fullName: string;
  email: string;
  phone?: string;
  company?: string;
  country: string;
  websiteUrl?: string;
  preferredCommunication?: string;
  additionalNotes?: string;
}

export interface JobApplicationFormData {
  fullName: string;
  email: string;
  phone: string;
  jobId: string;
  jobTitle: string;
  workPreference: string;
  experienceYears: string;
  currentLocation?: string;
  portfolioUrl?: string;
  resumeFileName?: string;
  resumeLink?: string;
  coverNote?: string;
  noticePeriod?: string;
  resumeFile?: File | null;
}

// Convert a File to Base64 data string
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Extract base64 payload after data:*/*;base64,
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Low-level dispatch to /api/send-email
 */
async function postToEmailApi(payload: {
  formType: 'contact' | 'consultation' | 'project_scoping' | 'career';
  senderName: string;
  senderEmail: string;
  phone?: string;
  company?: string;
  subject?: string;
  data: Record<string, any>;
  attachments?: Array<{
    filename: string;
    content: string; // base64
    contentType?: string;
  }>;
}): Promise<ApiResponse> {
  try {
    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorJson = JSON.parse(errorText);
        return {
          success: false,
          error: errorJson.error || `Server responded with status ${response.status}`,
        };
      } catch {
        return {
          success: false,
          error: `Server responded with status ${response.status}: ${errorText}`,
        };
      }
    }

    const data: ApiResponse = await response.json();
    return data;
  } catch (err: any) {
    console.error('Email API request failed:', err);
    return {
      success: false,
      error: err?.message || 'Network connection failed. Please try again or email us directly at info@ebonmedia.com.',
    };
  }
}

/**
 * 1. Send Quick Contact or Full Contact Form Inquiry
 */
export async function sendContactInquiry(data: ContactFormData): Promise<ApiResponse> {
  return postToEmailApi({
    formType: 'contact',
    senderName: data.fullName,
    senderEmail: data.email,
    phone: data.phone,
    company: data.company,
    subject: `[Inquiry] ${data.fullName} - ${data.service}`,
    data: {
      'Client Name': data.fullName,
      'Email Address': data.email,
      'Phone Number': data.phone || 'Not provided',
      'Company / Org': data.company || 'Not provided',
      'Requested Service': data.service,
      'Budget Range': data.budget || 'Flexible',
      'Target Region': data.region || 'International',
      'Timeline': data.timeline || 'Immediate',
      'Project Message': data.message,
    },
  });
}

/**
 * 2. Send Technical Consultation Request
 */
export async function sendConsultationRequest(data: ConsultationFormData): Promise<ApiResponse> {
  return postToEmailApi({
    formType: 'consultation',
    senderName: data.name,
    senderEmail: data.email,
    phone: data.phone,
    company: data.company,
    subject: `[Strategy Call] ${data.name} - ${data.service}`,
    data: {
      'Client Name': data.name,
      'Business Email': data.email,
      'Phone / WhatsApp': data.phone,
      'Company Name': data.company || 'Not provided',
      'Primary Focus': data.service,
      'Preferred Schedule': data.preferredTime || 'Flexible',
      'Estimated Budget': data.budget || 'Flexible',
      'Session Notes': data.notes || 'None provided',
    },
  });
}

/**
 * 3. Send Multi-Step Project Scoping Brief
 */
export async function sendProjectScopingBrief(data: ProjectScopingFormData): Promise<ApiResponse> {
  return postToEmailApi({
    formType: 'project_scoping',
    senderName: data.fullName,
    senderEmail: data.email,
    phone: data.phone,
    company: data.company,
    subject: `[Project Brief] ${data.fullName} (${data.country}) - ${data.service}`,
    data: {
      'Client Name': data.fullName,
      'Email Address': data.email,
      'Phone Number': data.phone || 'Not provided',
      'Company': data.company || 'Not provided',
      'Country / Region': data.country,
      'Target Service': data.service,
      'Project Scope & Goals': data.projectGoal,
      'Engagement Type': data.projectType,
      'Timeline Expectation': data.targetTimeline,
      'Budget Range': `${data.currency} ${data.budgetRange}`,
      'Current Website': data.websiteUrl || 'None',
      'Preferred Communication': data.preferredCommunication || 'Email',
      'Additional Notes': data.additionalNotes || 'None',
    },
  });
}

/**
 * 4. Send Job Application with Resume File Attachment
 */
export async function sendJobApplication(data: JobApplicationFormData): Promise<ApiResponse> {
  const attachments: Array<{ filename: string; content: string; contentType?: string }> = [];

  if (data.resumeFile) {
    try {
      const base64Content = await fileToBase64(data.resumeFile);
      attachments.push({
        filename: data.resumeFile.name,
        content: base64Content,
        contentType: data.resumeFile.type || 'application/octet-stream',
      });
    } catch (err) {
      console.warn('Failed to convert resume file to base64, continuing with link:', err);
    }
  }

  return postToEmailApi({
    formType: 'career',
    senderName: data.fullName,
    senderEmail: data.email,
    phone: data.phone,
    subject: `[Job Application] ${data.fullName} - ${data.jobTitle} (${data.workPreference})`,
    attachments,
    data: {
      'Candidate Name': data.fullName,
      'Email Address': data.email,
      'Contact Phone': data.phone,
      'Job ID & Role': `${data.jobId} - ${data.jobTitle}`,
      'Work Mode Preference': data.workPreference,
      'Experience Level': data.experienceYears,
      'Current Location': data.currentLocation || 'Not provided',
      'Notice Period': data.noticePeriod || 'Standard',
      'Portfolio / LinkedIn': data.portfolioUrl || 'None provided',
      'Resume Cloud Link': data.resumeLink || 'Uploaded directly via attachment',
      'Resume Attachment Name': data.resumeFileName || (data.resumeFile ? data.resumeFile.name : 'None'),
      'Cover Pitch / Intro': data.coverNote || 'None provided',
    },
  });
}
